/**
 * KeyboardControls.jsx
 * 
 * Composant pour contrôler la caméra OrbitControls avec le clavier.
 * 
 * Fonctionnalités :
 * - Mode Standard : Flèches = rotation, +/- = zoom
 * - Mode Zoom : Flèches ←→ = rotation, ↑↓ = zoom
 * - Système de damping pour mouvements fluides
 * - Respect des limites OrbitControls (distance, angles polaires)
 * 
 * @see examples/javascript/World/ControlsOrbit.js (référence vanilla)
 */

import { useEffect, useRef, useCallback } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { useControls } from 'leva'
import * as THREE from 'three'

function KeyboardControls() {
  const { camera, controls } = useThree()
  
  // Ref pour stocker les OrbitControls
  const orbitControlsRef = useRef(null)
  
  // Accès aux OrbitControls
  const getControls = useCallback(() => {
    // useThree().controls devrait contenir les OrbitControls avec makeDefault
    return controls || orbitControlsRef.current
  }, [controls])

  // État du mouvement clavier avec damping
  const keyboardMovement = useRef({
    targetTheta: 0,
    targetPhi: 0,
    targetDistance: 0,
    currentTheta: 0,
    currentPhi: 0,
    currentDistance: 0,
    active: false
  })

  // Contrôles Leva
  const { mode, rotateFactor, zoomFactor, easing } = useControls('Keyboard Controls', {
    mode: {
      value: 'standard',
      options: ['standard', 'zoom'],
      label: '⌨️ Mode Clavier'
    },
    rotateFactor: {
      value: 0.1,
      min: 0.01,
      max: 0.5,
      step: 0.01,
      label: 'Vitesse Rotation'
    },
    zoomFactor: {
      value: 1.1,
      min: 1.05,
      max: 1.5,
      step: 0.05,
      label: 'Facteur Zoom'
    },
    easing: {
      value: 0.15,
      min: 0.05,
      max: 0.3,
      step: 0.01,
      label: 'Damping'
    }
  },
  { collapsed: true }
  )

  /**
   * Initialise les valeurs de mouvement à partir de la position actuelle de la caméra
   * Calcul des angles sphériques (theta, phi, distance)
   */
  const initializeMovement = useCallback(() => {
    const controls = getControls()
    if (!controls) return

    // Calcul de l'offset depuis le target
    const offset = camera.position.clone().sub(controls.target)
    const distance = offset.length()

    // Calcul des angles sphériques
    // Theta : angle azimuthal (rotation horizontale autour de Z)
    // Phi : angle polaire (depuis le pôle Z)
    const theta = Math.atan2(offset.x, offset.y)
    const phi = Math.acos(Math.max(-1, Math.min(1, offset.z / distance)))

    keyboardMovement.current = {
      ...keyboardMovement.current,
      currentTheta: theta,
      targetTheta: theta,
      currentPhi: phi,
      targetPhi: phi,
      currentDistance: distance,
      targetDistance: distance
    }
  }, [camera, getControls])

  /**
   * Gère le zoom (avant/arrière)
   * @param {string} direction - 'in' ou 'out'
   */
  const handleZoom = useCallback((direction) => {
    const controls = getControls()
    if (!controls) return

    if (!keyboardMovement.current.active) {
      initializeMovement()
    }

    const currentDistance = keyboardMovement.current.targetDistance
    let newDistance = currentDistance

    if (direction === 'in') {
      newDistance = currentDistance / zoomFactor
    } else {
      newDistance = currentDistance * zoomFactor
    }

    // Application des limites de distance
    newDistance = Math.max(
      controls.minDistance,
      Math.min(controls.maxDistance, newDistance)
    )

    keyboardMovement.current.targetDistance = newDistance
    keyboardMovement.current.active = true
  }, [getControls, zoomFactor, initializeMovement])

  /**
   * Gère la rotation avec les flèches
   * @param {string} code - Code de la touche (ArrowUp, ArrowDown, ArrowLeft, ArrowRight)
   */
  const handleArrowRotation = useCallback((code) => {
    const controls = getControls()
    if (!controls) return

    if (!keyboardMovement.current.active) {
      initializeMovement()
    }

    let theta = keyboardMovement.current.targetTheta
    let phi = keyboardMovement.current.targetPhi

    switch (code) {
      case 'ArrowLeft':
        theta -= rotateFactor
        break
      case 'ArrowRight':
        theta += rotateFactor
        break
      case 'ArrowUp':
        phi -= rotateFactor
        // Limite l'angle polaire
        phi = Math.max(controls.minPolarAngle, Math.min(controls.maxPolarAngle, phi))
        break
      case 'ArrowDown':
        phi += rotateFactor
        // Limite l'angle polaire
        phi = Math.max(controls.minPolarAngle, Math.min(controls.maxPolarAngle, phi))
        break
    }

    keyboardMovement.current.targetTheta = theta
    keyboardMovement.current.targetPhi = phi
    keyboardMovement.current.active = true
  }, [getControls, rotateFactor, initializeMovement])

  /**
   * Gestionnaire principal des touches clavier
   */
  const handleKeyDown = useCallback((event) => {
    const controls = getControls()
    if (!controls?.enabled) return

    // Mode Standard : Flèches = rotation, +/- = zoom
    if (mode === 'standard') {
      switch (event.code) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          if (controls.enableRotate) {
            event.preventDefault()
            handleArrowRotation(event.code)
          }
          break
        case 'Equal': // Touche + (sans Shift)
        case 'NumpadAdd':
          if (controls.enableZoom) {
            event.preventDefault()
            handleZoom('in')
          }
          break
        case 'Minus':
        case 'NumpadSubtract':
          if (controls.enableZoom) {
            event.preventDefault()
            handleZoom('out')
          }
          break
      }
    }
    // Mode Zoom : Flèches ←→ = rotation, ↑↓ = zoom
    else if (mode === 'zoom') {
      switch (event.code) {
        case 'ArrowLeft':
        case 'ArrowRight':
          if (controls.enableRotate) {
            event.preventDefault()
            handleArrowRotation(event.code)
          }
          break
        case 'ArrowUp':
          if (controls.enableZoom) {
            event.preventDefault()
            handleZoom('in')
          }
          break
        case 'ArrowDown':
          if (controls.enableZoom) {
            event.preventDefault()
            handleZoom('out')
          }
          break
      }
    }
  }, [mode, getControls, handleArrowRotation, handleZoom])

  /**
   * useFrame pour l'interpolation (damping)
   * Appelé à chaque frame pour lisser les mouvements
   */
  useFrame(() => {
    const controls = getControls()
    if (!keyboardMovement.current.active || !controls) return

    const movement = keyboardMovement.current

    // Interpolation progressive (lerp) vers les valeurs cibles
    movement.currentTheta += (movement.targetTheta - movement.currentTheta) * easing
    movement.currentPhi += (movement.targetPhi - movement.currentPhi) * easing
    movement.currentDistance += (movement.targetDistance - movement.currentDistance) * easing

    // Calcul de la nouvelle position en coordonnées cartésiennes
    // x = distance * sin(φ) * sin(θ)
    // y = distance * sin(φ) * cos(θ)
    // z = distance * cos(φ)
    const offset = new THREE.Vector3(
      movement.currentDistance * Math.sin(movement.currentPhi) * Math.sin(movement.currentTheta),
      movement.currentDistance * Math.sin(movement.currentPhi) * Math.cos(movement.currentTheta),
      movement.currentDistance * Math.cos(movement.currentPhi)
    )

    // Mise à jour de la position de la caméra
    camera.position.copy(controls.target).add(offset)
    
    // IMPORTANT : Informer les OrbitControls de la mise à jour manuelle
    // Cela évite les conflits entre les contrôles clavier et souris
    controls.update()

    // Vérifier si on est proche de la cible (arrêt du mouvement)
    const deltaTheta = Math.abs(movement.targetTheta - movement.currentTheta)
    const deltaPhi = Math.abs(movement.targetPhi - movement.currentPhi)
    const deltaDistance = Math.abs(movement.targetDistance - movement.currentDistance)

    // Seuils pour désactiver l'interpolation
    if (deltaTheta < 0.001 && deltaPhi < 0.001 && deltaDistance < 0.01) {
      movement.active = false
    }
  })

  /**
   * Écoute des événements clavier
   * useCapture: true pour être prioritaire sur d'autres listeners
   */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [handleKeyDown])

  // Ce composant ne rend rien visuellement
  return null
}

export default KeyboardControls

